import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Card, Badge } from 'react-bootstrap';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { TicketsDataService } from '../../Services/TicketsDataService';
import './AdminSupportPage.scss';
import { formatGuatemalanDateTime } from '../../Utils/dateFormat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faBell, faEye, faUpload } from '@fortawesome/free-solid-svg-icons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { useNavigate } from 'react-router-dom';
import Table from '../../Components/Table/Table';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const AdminSupportPageContent = () => {
    const { instance, accounts, inProgress } = useMsal();
    const [tickets, setTickets] = useState([]);
    const [chartData, setChartData] = useState({
        labels: [],
        datasets: [
            {
                label: 'Opened Requests',
                data: [],
                backgroundColor: 'rgba(75, 192, 192, 1)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1,
            },
            {
                label: 'Closed Requests',
                data: [],
                backgroundColor: 'rgba(153, 102, 255, 1)',
                borderColor: 'rgba(153, 102, 255, 1)',
                borderWidth: 1,
            }
        ]
    });
    const navigate = useNavigate();

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchTickets();
            fetchMonthlyStats();
        }
    }, [inProgress, accounts, instance]);

    const fetchTickets = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const data = await TicketsDataService.getOpenTickets(response.accessToken);
            setTickets(data);
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
        }
    };

    const fetchMonthlyStats = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const data = await TicketsDataService.getMonthlyTicketStats(response.accessToken);
            prepareChartData(data);
        } catch (error) {
            console.error('Failed to fetch monthly ticket stats:', error);
        }
    };

    const handleViewTicket = (id) => {
        navigate(`/support/ticket/${id}`);
    };

    const prepareChartData = (data) => {
        if (!data || data.length === 0) {
            // Handle the case where data is empty or undefined
            console.error("No data available to prepare chart.");
            return;
        }
    
        const labels = data.map(d => {
            const date = new Date(d.year, d.month - 1);
            return date.toLocaleString('default', { month: 'long' });
        });
    
        const openedData = data.map(d => d.opened);
        const closedData = data.map(d => d.closed);
    
        setChartData({
            labels,
            datasets: [
                {
                    label: 'Opened Requests',
                    data: openedData,
                    backgroundColor: 'rgba(75, 192, 192, 1)',
                    borderColor: 'rgba(75, 192, 192, 1)',
                    borderWidth: 1
                },
                {
                    label: 'Completed/Closed Requests',
                    data: closedData,
                    backgroundColor: 'rgba(153, 102, 255, 1)',
                    borderColor: 'rgba(153, 102, 255, 1)',
                    borderWidth: 1
                }
            ]
        });
    };

    const handleNewTicketButton = () => {
        navigate('/support/ticket');
    }

    const statusClasses = {
        'new': 'primary',
        'in progress': 'warning',
        'user review': 'info',
        'completed': 'success',
        'closed': 'secondary'
    }

    const columns = [
        { header: 'ID', accessorKey: 'id' },
        { header: 'Subject', accessorKey: 'subject' },
        { header: 'Requester', accessorKey: 'requesterEmail' },
        { header: 'Opened', accessorKey: 'creationDate', cell: info => formatGuatemalanDateTime(info.getValue()) },
        { header: 'Updated', accessorKey: 'updatedDate', cell: info => formatGuatemalanDateTime(info.getValue()) },
        {
            header: 'Status',
            accessorKey: 'status',
            cell: (info) => (
                <div className='text-center'>
                    <Badge pill bg={statusClasses[info.getValue()]} className={`badge text-bg-${statusClasses[info.getValue()]}`}>
                        {info.getValue()}
                    </Badge>
                </div>
            )
        },
        {
            header: 'Actions',
            cell: ({ row }) => (
                <div className='text-center'>
                    <button className='btn btn-xs btn-outline-info' onClick={() => handleViewTicket(row.original.id)}>
                        <FontAwesomeIcon icon={faEye} />
                    </button>
                </div>
            )
        }
    ];
    

    return (
        <Container fluid className='admin-support-page h-100'>
            <Row className='h-100'>
                <Col md={8} className='d-flex flex-column'>
                    <Card className='flex-1'>
                        <Card.Body>
                            <h5>Open requests</h5>
                            <div className='table-container'>
                                <Table columns={columns} data={tickets} tableClass="table table-sm" containerClass="table-responsive" headerClass="table-light fit-header" />
                            </div>
                            {/* <Table columns={columns} data={tickets} tableClass="table-hover table-sm" containerClass="table-responsive" headerClass="table-light" /> */}
                        </Card.Body>
                    </Card>
                </Col>              
                <Col md={4} className='h-100'>
                    <div className='d-flex flex-column justify-content-between h-50 pb-4'>
                        <Card className="mb-0 button-card bg-primary" onClick={handleNewTicketButton}>
                            <Card.Body>
                                <h6 className='my-0 text-dark'>Create new support ticket</h6>
                                <div className='d-flex justify-content-end'>
                                    <FontAwesomeIcon icon={faArrowRight} size='lg' className='text-info' />
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className="mb-0 button-card" onClick={() => navigate('/support/open-requests')}>
                            <Card.Body>
                                <h6 className='my-0'>See All Requests</h6>
                                <div className='d-flex justify-content-end'>
                                    <FontAwesomeIcon icon={faArrowRight} size='lg' className='text-primary' />
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className="mb-0 button-card" onClick={() => navigate('/support/monthly-requests')}>
                            <Card.Body>
                                <h6 className='my-0'>See Monthly Requests</h6>
                                <div className='d-flex justify-content-end'>
                                    <FontAwesomeIcon icon={faArrowRight} size='lg' className='text-primary' />
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className='h-50 d-flex flex-column'>
                    <Card className='flex-1'>
                        <Card.Body>
                            <h6>Monthly Requests</h6>
                            <Bar data={chartData} />
                        </Card.Body>
                    </Card>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

const AdminSupportPage = () => {
    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
            <PageLayout2 title="Support Tickets">
                <AdminSupportPageContent />
            </PageLayout2>
        </MsalAuthenticationTemplate>
    );
};

export default AdminSupportPage;
